import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { apiErr } from "../lib/api";
import { Logo } from "../components/brand/Logo";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { ArrowLeft, CheckCircle2 } from "lucide-react";

export default function ResetPassword() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password !== confirm) { setError("Las contraseñas no coinciden"); return; }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      setDone(true);
      toast.success("Contraseña actualizada. Ya puedes iniciar sesión.");
    } catch (err) { setError(apiErr(err)); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-obsidian grid-bg flex flex-col">
      <div className="p-6">
        <button onClick={() => navigate("/auth")} className="inline-flex items-center gap-2 text-slate-400 hover:text-white text-sm">
          <ArrowLeft className="h-4 w-4" /> Ir a iniciar sesión
        </button>
      </div>
      <div className="flex-1 flex items-center justify-center px-4 pb-16">
        <div className="w-full max-w-md">
          <div className="flex justify-center mb-8"><Logo size="lg" /></div>
          <div className="card-arch p-8" data-testid="reset-password-card">
            <div className="halo" />
            {done ? (
              <div className="relative text-center space-y-5">
                <CheckCircle2 className="mx-auto h-12 w-12 text-emerald-400" />
                <h1 className="font-head text-2xl font-bold text-white">Contraseña actualizada</h1>
                <p className="text-sm text-slate-400">Tu contraseña se ha cambiado correctamente.</p>
                <Button data-testid="reset-go-login-button" onClick={() => navigate("/auth")} className="w-full bg-amber-500 text-black hover:bg-amber-400 rounded-lg font-semibold h-11">
                  Iniciar sesión
                </Button>
              </div>
            ) : !token ? (
              <div className="relative text-center space-y-4">
                <h1 className="font-head text-2xl font-bold text-white">Enlace no válido</h1>
                <p className="text-sm text-slate-400">El enlace de recuperación no es válido o está incompleto. Solicita uno nuevo desde la pantalla de inicio de sesión.</p>
                <Button data-testid="reset-request-new-button" onClick={() => navigate("/auth")} className="w-full bg-amber-500 text-black hover:bg-amber-400 rounded-lg font-semibold h-11">
                  Volver
                </Button>
              </div>
            ) : (
              <form onSubmit={submit} className="relative space-y-5">
                <h1 className="font-head text-2xl font-bold text-white">Nueva contraseña</h1>
                <div className="space-y-2">
                  <Label>Nueva contraseña</Label>
                  <Input data-testid="reset-password-input" type="password" required minLength={6} value={password}
                    onChange={(e) => setPassword(e.target.value)} className="bg-white/[0.03] border-white/10" placeholder="Mínimo 6 caracteres" />
                </div>
                <div className="space-y-2">
                  <Label>Confirmar contraseña</Label>
                  <Input data-testid="reset-confirm-input" type="password" required minLength={6} value={confirm}
                    onChange={(e) => setConfirm(e.target.value)} className="bg-white/[0.03] border-white/10" placeholder="Repite la contraseña" />
                </div>
                {error && <p data-testid="reset-error" className="text-sm text-red-400">{error}</p>}
                <Button data-testid="reset-submit-button" disabled={loading} className="w-full bg-amber-500 text-black hover:bg-amber-400 rounded-lg font-semibold h-11">
                  {loading ? "Un momento…" : "Cambiar contraseña"}
                </Button>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
